import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'
import AthleteDetail from './AthleteDetail'
import NotificationBell from '../../components/shared/NotificationBell'
import { triggerDailyNotifications } from '../../lib/notifications'

const SCHEDULE_TODAY = (() => {
  const day = new Date().getDay()
  const map = {
    0: { label: 'Long Ride', icon: '🗺️', color: '#60a5fa' },
    1: { label: 'MTB / XC', icon: '🚵', color: '#34d399' },
    2: { label: 'Road Passing', icon: '⚡', color: '#facc15' },
    3: { label: 'Road Passing', icon: '⚡', color: '#facc15' },
    4: { label: 'MTB / XC', icon: '🚵', color: '#34d399' },
    5: { label: 'Indoor Trainer', icon: '🎯', color: '#a78bfa' },
    6: { label: 'Hill Repeat', icon: '🏔️', color: '#f87171' },
  }
  return map[day]
})()

const WEEK_SCHEDULE = [
  { day: 'Sen', label: 'MTB/XC', icon: '🚵', color: '#34d399' },
  { day: 'Sel', label: 'Road', icon: '⚡', color: '#facc15' },
  { day: 'Rab', label: 'Road', icon: '⚡', color: '#facc15' },
  { day: 'Kam', label: 'MTB/XC', icon: '🚵', color: '#34d399' },
  { day: 'Jum', label: 'Indoor', icon: '🎯', color: '#a78bfa' },
  { day: 'Sab', label: 'Hill', icon: '🏔️', color: '#f87171' },
  { day: 'Min', label: 'Long Ride', icon: '🗺️', color: '#60a5fa' },
]

const statusConfig = {
  'top':              { label: '🔥 Top', color: '#facc15', bg: '#facc1522' },
  'aktif':            { label: '✅ Aktif', color: '#34d399', bg: '#34d39922' },
  'perlu-perhatian':  { label: '⚠️ Perlu Perhatian', color: '#f87171', bg: '#f8717122' },
}

const AVATAR_LIST = ['🚴','🚵','🏔️','⚡','🎯','🌟','💨','🔥','⭐','🏅']

// ─── Hitung status atlet dari stats ─────────────────────────────────────────
function calcStatus(stats) {
  if (!stats || stats.totalSesi === 0) return 'perlu-perhatian'
  if (stats.streak >= 7 && stats.rpe >= 8) return 'top'
  if (stats.streak === 0 || stats.totalSesi < 5) return 'perlu-perhatian'
  return 'aktif'
}

// ─── Hitung streak dari array tanggal sesi ──────────────────────────────────
function calcStreak(dates) {
  if (!dates || dates.length === 0) return 0
  const sorted = [...new Set(dates.map(d => d.slice(0, 10)))].sort().reverse()
  const today = new Date().toISOString().slice(0, 10)
  let streak = 0
  let cursor = new Date(today)

  for (const d of sorted) {
    const diff = Math.round((cursor - new Date(d)) / 86400000)
    if (diff <= 1) {
      streak++
      cursor = new Date(d)
    } else break
  }
  return streak
}

export default function CoachDashboard({ profile }) {
  const [athletes, setAthletes]         = useState([])
  const [loading, setLoading]           = useState(true)
  const [selectedAthlete, setSelectedAthlete] = useState(null)
  const [selectedDetailId, setSelectedDetailId] = useState(null)
  const [sortBy, setSortBy]             = useState('status')
  const [hovered, setHovered]           = useState(null)

  const todayIdx = new Date().getDay() === 0 ? 6 : new Date().getDay() - 1

  // ─── Trigger notifikasi harian saat coach buka dashboard ──────────────────
  useEffect(() => {
    if (profile?.id) {
      triggerDailyNotifications()
    }
  }, [profile?.id])

  // ─── Fetch atlet dari Supabase ─────────────────────────────────────────────
  useEffect(() => {
    const fetchAthletes = async () => {
      setLoading(true)

      // 1. Ambil semua atlet + profil
      const { data: athleteRows, error } = await supabase
        .from('athletes')
        .select(`
          id,
          category,
          profiles!athletes_profile_id_fkey (
            id,
            full_name,
            role
          )
        `)

      if (error) {
        console.error('Fetch athletes error:', error)
        setLoading(false)
        return
      }

      // Filter hanya role athlete, skip kalau profiles null
      const filtered = (athleteRows || []).filter(
        a => a.profiles && (a.profiles.role === 'athlete' || a.profiles.role === 'atlet')
      )

      // 2. Ambil semua session_logs sekaligus (1 query, bukan N query)
      const athleteIds = filtered.map(a => a.id)

      const { data: logs } = await supabase
        .from('session_logs')
        .select('athlete_id, rpe, cadence, avg_speed, heart_rate, created_at')
        .in('athlete_id', athleteIds)
        .order('created_at', { ascending: false })

      // 3. Gabungkan data per atlet
      const result = filtered.map((a, idx) => {
        const myLogs = (logs || []).filter(l => l.athlete_id === a.id)
        const latest = myLogs[0]

        const avgRpe     = myLogs.length > 0
          ? Math.round(myLogs.slice(0, 5).reduce((s, l) => s + (l.rpe || 0), 0) / Math.min(myLogs.length, 5))
          : 0
        const avgCadence = myLogs.length > 0
          ? Math.round(myLogs.slice(0, 5).filter(l => l.cadence).reduce((s, l) => s + l.cadence, 0) / Math.max(myLogs.slice(0, 5).filter(l => l.cadence).length, 1))
          : 0
        const avgSpeed   = myLogs.length > 0
          ? parseFloat((myLogs.slice(0, 5).filter(l => l.avg_speed).reduce((s, l) => s + l.avg_speed, 0) / Math.max(myLogs.slice(0, 5).filter(l => l.avg_speed).length, 1)).toFixed(1))
          : 0
        const avgHR      = myLogs.length > 0
          ? Math.round(myLogs.slice(0, 5).filter(l => l.heart_rate).reduce((s, l) => s + l.heart_rate, 0) / Math.max(myLogs.slice(0, 5).filter(l => l.heart_rate).length, 1))
          : 0

        const streak = calcStreak(myLogs.map(l => l.created_at))

        const stats = {
          totalSesi: myLogs.length,
          rpe:       avgRpe,
          cadence:   avgCadence,
          speed:     avgSpeed,
          hr:        avgHR,
          streak,
        }

        return {
          id:        a.id,
          profileId: a.profiles.id,       // ← INI yang dibutuhkan tombol Lihat Detail
          name:      a.profiles.full_name,
          category:  a.category || '-',
          avatar:    AVATAR_LIST[idx % AVATAR_LIST.length],
          status:    calcStatus(stats),
          ...stats,
        }
      })

      setAthletes(result)
      setLoading(false)
    }

    fetchAthletes()
  }, [])

  // ─── Sort ──────────────────────────────────────────────────────────────────
  const sorted = [...athletes].sort((a, b) => {
    if (sortBy === 'status') {
      const order = { 'top': 0, 'aktif': 1, 'perlu-perhatian': 2 }
      return order[a.status] - order[b.status]
    }
    if (sortBy === 'streak') return b.streak - a.streak
    if (sortBy === 'sesi')   return b.totalSesi - a.totalSesi
    if (sortBy === 'rpe')    return b.rpe - a.rpe
    return 0
  })

  const needAttention = athletes.filter(a => a.status === 'perlu-perhatian')

  // ─── Navigasi ke AthleteDetail ─────────────────────────────────────────────
  if (selectedDetailId) {
    return (
      <AthleteDetail
        athleteProfileId={selectedDetailId}
        onBack={() => setSelectedDetailId(null)}
      />
    )
  }

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: '100vh', background: '#0f172a', color: '#f1f5f9', maxWidth: '100%', overflowX: 'hidden', boxSizing: 'border-box' }}>

      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '1rem 1.5rem',
        borderBottom: '1px solid #1e293b',
        background: '#0a0f1e',
        overflow: 'hidden',
        boxSizing: 'border-box'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', minWidth: 0 }}>
          <img src="/logo_rollaco.png" alt="Rollaco"
            style={{ width: '40px', height: '40px', objectFit: 'contain' }} />
          <div>
            <div style={{ color: '#34d399', fontWeight: 'bold', fontSize: '1rem', letterSpacing: '0.05em' }}>
              ROLLACO
            </div>
            <div style={{ color: '#94a3b8', fontSize: '0.65rem', letterSpacing: '0.1em' }}>
              COACH PANEL
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexShrink: 0 }}>
          <div style={{ textAlign: 'right', minWidth: 0 }}>
            <div style={{ color: '#f1f5f9', fontSize: '0.9rem', fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '120px' }}>
              {profile?.full_name}
            </div>
            <div style={{ color: '#34d399', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Coach
            </div>
          </div>
          {/* ── Notification Bell ── */}
          {profile?.id && <NotificationBell userId={profile.id} />}
          <button
            onClick={() => supabase.auth.signOut()}
            style={{
              background: '#1e293b', border: '1px solid #334155',
              borderRadius: '8px', color: '#94a3b8',
              padding: '0.4rem 0.75rem', cursor: 'pointer', fontSize: '0.8rem'
            }}
          >
            Keluar
          </button>
        </div>
      </div>

      <div style={{ padding: '1rem 1rem', overflowX: 'hidden', boxSizing: 'border-box' }}>

        {/* Summary cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))', gap: '0.5rem', marginBottom: '1rem' }}>
          <div style={{
            background: `${SCHEDULE_TODAY.color}18`,
            border: `2px solid ${SCHEDULE_TODAY.color}`,
            borderRadius: '12px', padding: '1rem',
            textAlign: 'center', color: SCHEDULE_TODAY.color
          }}>
            <div style={{ fontSize: '1.8rem' }}>{SCHEDULE_TODAY.icon}</div>
            <div style={{ fontWeight: 'bold', fontSize: '0.85rem', marginTop: '0.25rem' }}>Sesi Hari Ini</div>
            <div style={{ fontSize: '1rem', fontWeight: 'bold' }}>{SCHEDULE_TODAY.label}</div>
          </div>

          <div style={{
            background: '#38bdf818', border: '2px solid #38bdf8',
            borderRadius: '12px', padding: '1rem',
            textAlign: 'center', color: '#38bdf8'
          }}>
            <div style={{ fontSize: '1.8rem' }}>👥</div>
            <div style={{ fontWeight: 'bold', fontSize: '0.85rem', marginTop: '0.25rem' }}>Total Atlet</div>
            <div style={{ fontSize: '1.8rem', fontWeight: 'bold' }}>
              {loading ? '…' : athletes.length}
            </div>
          </div>

          <div style={{
            background: '#f8717118', border: '2px solid #f87171',
            borderRadius: '12px', padding: '1rem',
            textAlign: 'center', color: '#f87171'
          }}>
            <div style={{ fontSize: '1.8rem' }}>⚠️</div>
            <div style={{ fontWeight: 'bold', fontSize: '0.85rem', marginTop: '0.25rem' }}>Perlu Perhatian</div>
            <div style={{ fontSize: '1.8rem', fontWeight: 'bold' }}>
              {loading ? '…' : needAttention.length}
            </div>
          </div>
        </div>

        {/* Jadwal minggu */}
        <div style={{
          background: '#a78bfa18', border: '2px solid #a78bfa',
          borderRadius: '12px', padding: '0.75rem 1rem', marginBottom: '1rem'
        }}>
          <div style={{ color: '#a78bfa', fontWeight: 'bold', fontSize: '0.8rem', marginBottom: '0.5rem' }}>
            📅 Jadwal Minggu Ini
          </div>
          <div style={{ display: 'flex', gap: '4px', overflowX: 'hidden' }}>
            {WEEK_SCHEDULE.map((s, i) => (
              <div key={s.day} style={{
                flex: 1, textAlign: 'center',
                background: i === todayIdx ? s.color : `${s.color}22`,
                borderRadius: '6px', padding: '4px 2px',
                border: i === todayIdx ? `2px solid ${s.color}` : '2px solid transparent'
              }}>
                <div style={{ fontSize: '0.85rem' }}>{s.icon}</div>
                <div style={{
                  fontSize: '0.5rem', fontWeight: 'bold',
                  color: i === todayIdx ? '#0f172a' : s.color
                }}>
                  {s.day}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Sort controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
          <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>Urutkan:</span>
          {['status', 'streak', 'sesi', 'rpe'].map(opt => (
            <button key={opt} onClick={() => setSortBy(opt)} style={{
              padding: '0.25rem 0.6rem', borderRadius: '999px',
              border: sortBy === opt ? '2px solid #34d399' : '2px solid #334155',
              background: sortBy === opt ? '#34d39920' : 'transparent',
              color: sortBy === opt ? '#34d399' : '#94a3b8',
              cursor: 'pointer', fontSize: '0.7rem',
              fontWeight: sortBy === opt ? 'bold' : 'normal',
              textTransform: 'capitalize'
            }}>
              {opt}
            </button>
          ))}
        </div>

        {/* Loading state */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '3rem 0', color: '#94a3b8' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>⏳</div>
            <div style={{ fontSize: '0.85rem' }}>Memuat data atlet...</div>
          </div>
        )}

        {/* Empty state */}
        {!loading && athletes.length === 0 && (
          <div style={{ textAlign: 'center', padding: '3rem 0', color: '#94a3b8' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>👥</div>
            <div style={{ fontSize: '0.85rem' }}>Belum ada atlet terdaftar.</div>
          </div>
        )}

        {/* Athlete list */}
        {!loading && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {sorted.map(athlete => {
              const sc = statusConfig[athlete.status]
              return (
                <div
                  key={athlete.id}
                  onClick={() => setSelectedAthlete(athlete)}
                  onMouseEnter={() => setHovered(athlete.id)}
                  onMouseLeave={() => setHovered(null)}
                  style={{
                    background: hovered === athlete.id ? '#1e293b' : '#0f172a',
                    border: `1px solid ${athlete.status === 'perlu-perhatian' ? '#f8717155' : '#1e293b'}`,
                    borderRadius: '12px', padding: '0.75rem 1rem',
                    cursor: 'pointer', transition: 'all 0.2s',
                    display: 'grid',
                    gridTemplateColumns: '2.5rem 1fr auto',
                    alignItems: 'center', gap: '0.75rem'
                  }}
                >
                  {/* Avatar */}
                  <div style={{
                    width: '2.5rem', height: '2.5rem', borderRadius: '50%',
                    background: '#1e293b', border: `2px solid ${sc.color}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '1.2rem'
                  }}>
                    {athlete.avatar}
                  </div>

                  {/* Info */}
                  <div style={{ minWidth: 0, overflow: 'hidden' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 'bold', fontSize: '0.9rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '120px' }}>{athlete.name}</span>
                      <span style={{
                        fontSize: '0.65rem', padding: '1px 6px',
                        borderRadius: '999px', background: sc.bg, color: sc.color
                      }}>
                        {sc.label}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.65rem', color: '#475569', marginBottom: '0.2rem' }}>
                      {athlete.category}
                    </div>
                    <div style={{ display: 'flex', gap: '1rem' }}>
                      <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>
                        🔥 <strong style={{ color: '#f1f5f9' }}>{athlete.streak}h</strong>
                      </span>
                      <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>
                        📊 RPE <strong style={{ color: '#fb923c' }}>{athlete.rpe || '-'}</strong>
                      </span>
                      <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>
                        ⚙️ <strong style={{ color: '#f472b6' }}>{athlete.cadence || '-'}</strong>rpm
                      </span>
                    </div>
                  </div>

                  {/* Speed */}
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#facc15' }}>
                      {athlete.speed || '-'}
                    </div>
                    <div style={{ fontSize: '0.6rem', color: '#94a3b8' }}>km/jam</div>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* Tombol input sesi */}
        <button style={{
          width: '100%', marginTop: '1rem', padding: '0.85rem',
          background: '#34d39920', border: '2px dashed #34d399',
          borderRadius: '12px', color: '#34d399',
          fontSize: '0.9rem', fontWeight: 'bold',
          cursor: 'pointer', letterSpacing: '0.05em'
        }}>
          + Input Sesi Hari Ini
        </button>
      </div>

      {/* Modal detail atlet */}
      {selectedAthlete && (
        <div
          onClick={() => setSelectedAthlete(null)}
          style={{
            position: 'fixed', inset: 0, background: '#00000088',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 100, padding: '1rem'
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#1e293b',
              border: `2px solid ${statusConfig[selectedAthlete.status].color}`,
              borderRadius: '16px', padding: '1.5rem',
              maxWidth: '320px', width: '100%',
              boxShadow: `0 0 30px ${statusConfig[selectedAthlete.status].color}33`
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
              <div style={{
                width: '3.5rem', height: '3.5rem', borderRadius: '50%',
                background: '#0f172a',
                border: `2px solid ${statusConfig[selectedAthlete.status].color}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '1.8rem'
              }}>
                {selectedAthlete.avatar}
              </div>
              <div>
                <div style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>{selectedAthlete.name}</div>
                <div style={{ color: '#94a3b8', fontSize: '0.75rem' }}>{selectedAthlete.category}</div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '1rem' }}>
              {[
                { label: 'Total Sesi',    value: selectedAthlete.totalSesi,          color: '#38bdf8', icon: '📊' },
                { label: 'Streak',        value: `${selectedAthlete.streak} hari`,   color: '#fb923c', icon: '🔥' },
                { label: 'RPE Rata-rata', value: `${selectedAthlete.rpe || '-'}/10`, color: '#f472b6', icon: '💪' },
                { label: 'Cadence',       value: selectedAthlete.cadence ? `${selectedAthlete.cadence} rpm` : '-', color: '#a78bfa', icon: '⚙️' },
                { label: 'Avg Speed',     value: selectedAthlete.speed ? `${selectedAthlete.speed} km/j` : '-',    color: '#facc15', icon: '⚡' },
                { label: 'Avg HR',        value: selectedAthlete.hr ? `${selectedAthlete.hr} bpm` : '-',           color: '#ef4444', icon: '❤️' },
              ].map(stat => (
                <div key={stat.label} style={{
                  background: '#0f172a', borderRadius: '8px', padding: '0.6rem',
                  border: `1px solid ${stat.color}33`
                }}>
                  <div style={{ fontSize: '0.65rem', color: '#94a3b8' }}>{stat.icon} {stat.label}</div>
                  <div style={{ fontSize: '1rem', fontWeight: 'bold', color: stat.color, marginTop: '2px' }}>
                    {stat.value}
                  </div>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={() => {
                  setSelectedDetailId(selectedAthlete.profileId)  // ← profileId real dari DB
                  setSelectedAthlete(null)
                }}
                style={{
                  flex: 1, padding: '0.5rem',
                  background: '#34d39920', border: '1px solid #34d399',
                  borderRadius: '8px', color: '#34d399',
                  cursor: 'pointer', fontSize: '0.8rem', fontWeight: 'bold'
                }}
              >
                Lihat Detail
              </button>
              <button
                onClick={() => setSelectedAthlete(null)}
                style={{
                  padding: '0.5rem 1rem',
                  background: '#334155', border: 'none',
                  borderRadius: '8px', color: '#94a3b8',
                  cursor: 'pointer', fontSize: '0.8rem'
                }}
              >
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}